export enum ClientDocumentStatus {
    PENDING = 'Pending',
    APPROVED = 'Approved',
    REJECTED = 'Rejected'
}

export enum DocumentStatus {
    PENDING = 0,
    APPROVED = 1,
    REJECTED = 2
}

export enum ClientDocumentVerificationStatus {
    NOT_VERIFIED = 'NotVerified',
    PENDING_VERIFICATION = 'PendingVerification',
    PARTIALLY_VERIFIED = 'PartiallyVerified',
    FULLY_VERIFIED = 'FullyVerified'
}

export interface ClientDocumentVerificationStatusDetail {
    label: string;
    description: string;
    badgeClass: string;
}

export const clientDocumentVerificationStatusDetails: Record<ClientDocumentVerificationStatus, ClientDocumentVerificationStatusDetail> = {
    [ClientDocumentVerificationStatus.NOT_VERIFIED]: {
        label: 'Not Verified',
        description: 'Mandatory documents not uploaded',
        badgeClass: 'profile-verified-badge--neutral'
    },
    [ClientDocumentVerificationStatus.PENDING_VERIFICATION]: {
        label: 'Pending Verification',
        description: 'Mandatory documents uploaded, awaiting compliance approval',
        badgeClass: 'profile-verified-badge--warning'
    },
    [ClientDocumentVerificationStatus.PARTIALLY_VERIFIED]: {
        label: 'Partially Verified',
        description: 'Mandatory documents approved, optional documents still pending',
        badgeClass: 'profile-verified-badge--info'
    },
    [ClientDocumentVerificationStatus.FULLY_VERIFIED]: {
        label: 'Verified',
        description: 'All documents uploaded and approved',
        badgeClass: 'profile-verified-badge--success'
    }
};

export const clientDocumentVerificationStatusDescriptions: Record<ClientDocumentVerificationStatus, string> = {
    [ClientDocumentVerificationStatus.NOT_VERIFIED]: clientDocumentVerificationStatusDetails[ClientDocumentVerificationStatus.NOT_VERIFIED].description,
    [ClientDocumentVerificationStatus.PENDING_VERIFICATION]: clientDocumentVerificationStatusDetails[ClientDocumentVerificationStatus.PENDING_VERIFICATION].description,
    [ClientDocumentVerificationStatus.PARTIALLY_VERIFIED]: clientDocumentVerificationStatusDetails[ClientDocumentVerificationStatus.PARTIALLY_VERIFIED].description,
    [ClientDocumentVerificationStatus.FULLY_VERIFIED]: clientDocumentVerificationStatusDetails[ClientDocumentVerificationStatus.FULLY_VERIFIED].description
};

export enum UploadDocumentType {
    CERTIFICATE_OF_INCORPORATION = 1,
    TAX_REGISTRATION_INFORMATION = 2,
    PROOF_OF_REGISTERED_BUSINESS_ADDRESS = 3,
    CONSTITUTIONAL_DOCUMENTS = 4,
    CORPORATE_REGISTRY_EXTRACT = 5,
    REGISTER_OF_DIRECTORS = 6
}
 
export enum DocumentType {
    CERTIFICATE_OF_INCORPORATION = 'CertificateOfIncorporation',
    TAX_REGISTRATION_INFORMATION = 'TaxRegistrationInformation',
    PROOF_OF_REGISTERED_BUSINESS_ADDRESS = 'ProofOfRegisteredBusinessAddress',
    CONSTITUTIONAL_DOCUMENTS = 'ConstitutionalDocuments',
    CORPORATE_REGISTRY_EXTRACT = 'CorporateRegistryExtract',
    REGISTER_OF_DIRECTORS = 'RegisterOfDirectors' 
}

export interface IClientDocumentMeta {
    documentType: DocumentType;
    icon: string;
    title: string;
    description: string;
    mandatory: boolean;
    fileName: string | null;
    documentUrl: string | null;
    status: ClientDocumentStatus | null;
    reviewComment: string | null;
    clientName?: string | null;
}

export const clientDocumentMetadata: IClientDocumentMeta[] = [
    {
        documentType: DocumentType.CERTIFICATE_OF_INCORPORATION,
        icon: 'bi-file-earmark-text',
        title: 'Certificate of Incorporation/Registration',
        description: 'Certificate issued by the relevant corporate registry confirming the legal incorporation or registration of the organization.',
        mandatory: true,
        fileName: null,
        documentUrl: null,
        status: null,
        reviewComment: null
    },
    {
        documentType: DocumentType.TAX_REGISTRATION_INFORMATION,
        icon: 'bi-briefcase',
        title: 'Tax Registration Information',
        description: 'Certificate issued by the relevant corporate registry confirming the legal incorporation or registration of the organization.',
        mandatory: true,
        fileName: null,
        documentUrl: null,
        status: null,
        reviewComment: null
    },
    {
        documentType: DocumentType.PROOF_OF_REGISTERED_BUSINESS_ADDRESS,
        icon: 'bi-file-earmark-text',
        title: 'Proof of Registered Business Address',
        description: 'Recent utility bill, lease agreement, or other document confirming the registered business address.',
        mandatory: true,
        fileName: null,
        documentUrl: null,
        status: null,
        reviewComment: null
    },
    {
        documentType: DocumentType.CONSTITUTIONAL_DOCUMENTS,
        icon: 'bi-file-earmark-ruled',
        title: 'Constitutional Documents',
        description: 'Memorandum and Articles of Association, Articles of Incorporation, Constitution, or equivalent governing documents.',
        mandatory: false,
        fileName: null,
        documentUrl: null,
        status: null,
        reviewComment: null
    },
    {
        documentType: DocumentType.CORPORATE_REGISTRY_EXTRACT,
        icon: 'bi-clipboard-check',
        title: 'Corporate Registry Extract',
        description: 'A current extract or status report issued by the relevant corporate registry showing the organization\'s registration status and registered details. (e.g CAC Status Report or Company Extract)',
        mandatory: false,
        fileName: null,
        documentUrl: null,
        status: null,
        reviewComment: null
    },
    {
        documentType: DocumentType.REGISTER_OF_DIRECTORS,
        icon: 'bi-people',
        title: 'Register of Directors or Directors\' Particulars',
        description: 'The current register or official filing identifying the organization\'s directors or equivalent officers.',
        mandatory: false,
        fileName: null,
        documentUrl: null,
        status: null,
        reviewComment: null
    }
];

const uploadDocumentTypeMap: Record<DocumentType, UploadDocumentType> = {
    [DocumentType.CERTIFICATE_OF_INCORPORATION]: UploadDocumentType.CERTIFICATE_OF_INCORPORATION,
    [DocumentType.TAX_REGISTRATION_INFORMATION]: UploadDocumentType.TAX_REGISTRATION_INFORMATION,
    [DocumentType.PROOF_OF_REGISTERED_BUSINESS_ADDRESS]: UploadDocumentType.PROOF_OF_REGISTERED_BUSINESS_ADDRESS,
    [DocumentType.CONSTITUTIONAL_DOCUMENTS]: UploadDocumentType.CONSTITUTIONAL_DOCUMENTS,
    [DocumentType.CORPORATE_REGISTRY_EXTRACT]: UploadDocumentType.CORPORATE_REGISTRY_EXTRACT,
    [DocumentType.REGISTER_OF_DIRECTORS]: UploadDocumentType.REGISTER_OF_DIRECTORS
};

export function toUploadDocumentType(documentType: DocumentType): UploadDocumentType {
    return uploadDocumentTypeMap[documentType];
}

export interface IClientDocument {
    clientId?: string;
    verificationStatus?: ClientDocumentVerificationStatus;
    documents?: IClientDocumentItem[];
}

export interface IClientDocumentItem {
    documentType: DocumentType;
    fileName: string;
    fileType: string;
    documentUrl: string;
    status: ClientDocumentStatus;
    uploadedAt: Date;
    reviewedAt?: Date;
    verifiedAt?: Date;
    reviewedBy?: string;
    reviewComment?: string;
    moreInfoRequested?: boolean;
    rejectionReason?: string;
    reviewHistory?: IClientDocumentReviewHistory[];
}

export interface IClientDocumentReviewHistory {
    reviewedBy: string;
    reviewedAt: Date;
    outcome: string;
    reviewComment?: string;
    moreInfoRequested?: boolean;
    rejectionReason?: string;
}

export interface IUploadClientDocument {
    documentType: UploadDocumentType;
    file: File;
}

export interface IReviewClientDocument {
    reviewComment?: string;
}

export interface IPendingClientRequestDocument {
    clientId: string;
    clientName: string;
    documentType: DocumentType;
    fileName: string;
    fileType: string;
    documentUrl: string;
    status: ClientDocumentStatus;
}

